import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  Snackbar,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Typography,
  useTheme
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../../components/Dashboard/Header";
import { tokens } from "../../../theme";
import axios from "axios";
import { schemeCategory10 } from 'd3-scale-chromatic';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

// Shared TextField styles
const textFieldStyles = {
  "& .MuiOutlinedInput-root": {
    fontSize: "0.9rem",
    height: "45px",
    "& fieldset": { borderColor: "#771726" },
    "&:hover fieldset": { borderColor: "#771726" },
    "&.Mui-focused fieldset": { borderColor: "#771726" },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.85rem",
    color: "#000000",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#000000",
  },
};

// Current datetime-helper
const nowLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmtNoSeconds = (v) =>
  typeof v === "string" ? v.replace(/:\d{2}$/, "") : v;

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1, bgcolor: '#222', border: '1px solid #555' }}>
      <Typography variant="caption" sx={{ color: '#ddd' }}>
        {fmtNoSeconds(label)}
      </Typography>
      {payload.map((entry) => (
        <Typography key={entry.dataKey} variant="body2" sx={{ color: entry.color }}>
          {entry.name || entry.dataKey}: {entry.value}
        </Typography>
      ))}
    </Paper>
  );
}

// SERIES (para checkboxes/leyenda/gráfico)
  const seriesOptions = [
    // Analógicas
    { key: "tempProcesoEntrada",        label: "Temp. Proceso Entrada" },
    { key: "tempProcesoSalida",         label: "Temp. Proceso Salida" },
    { key: "tempCondensacionEntrada",   label: "Temp. Condensación Entrada" },
    { key: "tempCondensacionSalida",    label: "Temp. Condensación Salida" },
    { key: "tempDescargaCompresor",     label: "Temp. Descarga Compresor" },
    { key: "tempRefrEvaporado",         label: "Temp. Refr Evaporado" },
    { key: "presionSuccion",            label: "Presión Succión" },
    { key: "presionDescarga",           label: "Presión Descarga" },
    { key: "voltajeCmp",                label: "Voltaje Comp." },
    { key: "amperajeCmp",               label: "Amperaje Comp." },
    { key: "sobrecalentamiento",        label: "Sobrecalentamiento" },
    { key: "condicionActual",           label: "Condición Actual" },
    { key: "porcentajeCarga",           label: "Porcentaje Carga (%)" },

    // Digitales
    { key: "controlCarga",              label: "Control de Carga" },
    { key: "ramploading",               label: "Ramp Loading" },
    { key: "switchFlujoAguaCondensacion", label: "Switch Flujo Agua Condensación" },
    { key: "switchFlujoAguaHelada",     label: "Switch Flujo Agua Helada" },
    { key: "bajoNivelAceiteComp",       label: "Bajo Nivel Aceite Comp." },
    { key: "statusTempMotor",           label: "Status Temp Motor" },
    { key: "interlock",                 label: "Interlock" },
    { key: "statusMotorProtect",        label: "Status MotorProtect" },
    { key: "arranqueRemoto",            label: "Arranque Remoto" },
    { key: "paroEmergencia",            label: "Paro de Emergencia" },
    { key: "encendidoCompresor",        label: "Encendido Compresor" },
    { key: "loadUnloadValvula1",        label: "Load/Unload Válvula 1" },
    { key: "loadUnloadValvula2",        label: "Load/Unload Válvula 2" },
    { key: "loadUnloadValvula3",        label: "Load/Unload Válvula 3" },
    { key: "solenoideGasCaliente",      label: "Solenoide de Gas Caliente" },
    { key: "inyeccionLiquidoSuccion",   label: "Inyección de Líquido en la Succión" },
  ];


const GLicuador1Comp = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Form state
  const [idEquipo, setIdEquipo] = useState("");
  const [fechaInicio, setFechaInicio] = useState(nowLocal());
  const [fechaFinal, setFechaFinal] = useState(nowLocal());

  // Tab state
  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = (_, newIndex) => setTabIndex(newIndex);

  // Data state
  const [rows, setRows] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE;
  const MACHINE  = "Licuador 1Comp";
  const QUERY    = "Grafica";


  // toggle selection
  const toggleKey = (k) => {
    setSelectedKeys((prev) =>
      prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]
    );
  };

  const fetchRows = async () => {
  // 1) validación de fechas
  if (new Date(fechaFinal) < new Date(fechaInicio)) {
    setError("La fecha final no puede ser anterior a la fecha de inicio.");
    return;
  }

  setError(null);
  setLoading(true);
  setRows([]);

  try {
    // 2) petición
    const res = await axios.post(
  `${API_BASE}/test/consulta/` +
    `${encodeURIComponent(MACHINE)}/` +
    `${encodeURIComponent(QUERY)}`,
  { idEquipo, fechaInicio, fechaFinal },
  { headers: { "Content-Type": "application/json" } }
  );


    // 3) parsear body
    const body = res.data.body
      ? (typeof res.data.body === "string"
          ? JSON.parse(res.data.body)
          : res.data.body)
      : res.data;

    //console.log("👉 body ya parseado:", JSON.stringify(body, null, 2));

    // 4) extraer array
    const extractedData = Array.isArray(body.data)
      ? body.data
      : (Array.isArray(body.data?.[0]) ? body.data[0] : []);

    // 5) limpiar claves
    const normalizeKeys = row => {
      const clean = {};
      for (const k in row) clean[k.trim()] = row[k];
      return clean;
    };
    
    // 6) mapeo completo (copia aquí TODOS tus campos)
    const mapped = extractedData.map((row, i) => {
      const r = normalizeKeys(row);
      const fechaUTC = new Date(r["Fecha"]);
      const ts = fechaUTC.getTime();
      const formattedDate = 
        `${fechaUTC.getUTCFullYear()}-${String(fechaUTC.getUTCMonth()+1).padStart(2,"0")}`+
        `-${String(fechaUTC.getUTCDate()).padStart(2,"0")} `+
        `${String(fechaUTC.getUTCHours()).padStart(2,"0")}:`+
        `${String(fechaUTC.getUTCMinutes()).padStart(2,"0")}:`+
        `${String(fechaUTC.getUTCSeconds()).padStart(2,"0")}`;

        const base = {
          id: i + 1,
          ts,
          tipoMaquinaControl: row["Tipo de Máquina y Control"],
          numeroEquipo: row["Número de Equipo"],
          fecha: formattedDate, // ✅ Se usa la fecha convertida aquí
          tempProcesoEntrada: row["Temp. Proceso Entrada"],
          tempProcesoSalida: row["Temp. Proceso Salida"],
          tempCondensacionEntrada: row["Temp. Condensación Entrada"],
          tempCondensacionSalida: row["Temp. Condensación Salida"],
          tempDescargaCompresor: row["Temp. Descarga Compresor"],
          tempRefrEvaporado: row["Temp. Refr Evaporado"],
          presionSuccion: row["Presion Succion"],
          presionDescarga: row["Presion Descarga"],
          voltajeCmp: row["Voltaje Cmp"],
          amperajeCmp: row["Amperaje Cmp"],
          sobrecalentamiento: row["Sobrecalentamiento"],
          condicionActual: row["Condicion Actual"],
          porcentajeCarga: row["Porcentaje Carga"],
          controlCarga: row["Control de Carga"],
          ramploading: row["Ramploading"],
          switchFlujoAguaCondensacion: row["Switch Flujo Agua Condensacion"],
          switchFlujoAguaHelada: row["Switch Flujo Agual Helada"],
          bajoNivelAceiteComp: row["Bajo Nivel Aceite Comp"],
          statusTempMotor: row["Status Temp Motor"],
          interlock: row["Interlock"],
          statusMotorProtect: row["Status MotorProtect"],
          arranqueRemoto: row["Arranque Remoto"],
          paroEmergencia: row["Paro de Emergencia"],
          encendidoCompresor: row["Encendido Compresor"],
          loadUnloadValvula1: row["Load/Unload Valvula 1 Comp. 1"],
          loadUnloadValvula2: row["Load/Unload Valvula 2 Comp. 1"],
          loadUnloadValvula3: row["Load/Unload Valvula 3 Comp. 1"],
          solenoideGasCaliente: row["Solenoide de Gas Caliente"],
          inyeccionLiquidoSuccion: row["Inyección de Líquido en la Succión"],
          };

    // 🔌 OPCIONAL (pero recomendable si tus datos vienen como strings/ON/OFF):
     //Normaliza todo lo que no sea texto a número/0-1
     const parseValue = (v) => {
      if (v === null || v === undefined || v === "") return null;
      const s = String(v).trim().toLowerCase();
       if (["true","on","encendido","sí","si","1"].includes(s)) return 1;
       if (["false","off","apagado","no","0"].includes(s)) return 0;
       const n = Number(s.replace(",", "."));
       return Number.isFinite(n) ? n : v;
    };
     const TEXT_KEYS = new Set(["id","tipoMaquinaControl","numeroEquipo","fecha","ts"]);
     Object.keys(base).forEach(k => {
       if (!TEXT_KEYS.has(k)) base[k] = parseValue(base[k]);
     });

    return base;
      })

    // 6.1) ✅ Ordena una sola vez aquí
    mapped.sort((a, b) => {
      const ta = a.ts ?? Date.parse(a.fecha) ?? 0;
      const tb = b.ts ?? Date.parse(b.fecha) ?? 0;
      return ta - tb; // ascendente: del más viejo al más nuevo
    });

      setRows(mapped);

  } catch (err) {
    console.error(err);
    setError("Error al consultar los datos. Intenta de nuevo.");
  } finally {
    setLoading(false);
  }
};

  // keys que corresponden a series análogas y digitales
  const analogKeys = [
    "tempProcesoEntrada", "tempProcesoSalida", "tempCondensacionEntrada", "tempCondensacionSalida",
    "tempDescargaCompresor", "tempRefrEvaporado", "presionSuccion", "presionDescarga",
    "voltajeCmp", "amperajeCmp", "sobrecalentamiento", "condicionActual",
    "porcentajeCarga",
  ];

  const digitalKeys = [
    "controlCarga", "ramploading", "switchFlujoAguaCondensacion", "switchFlujoAguaHelada",
    "bajoNivelAceiteComp", "statusTempMotor", "interlock", "statusMotorProtect",
    "arranqueRemoto", "paroEmergencia", "encendidoCompresor", "loadUnloadValvula1",
    "loadUnloadValvula2", "loadUnloadValvula3", "solenoideGasCaliente", "inyeccionLiquidoSuccion",
  ];


  // COLUMNAS (DataGrid)
  const columns = [
    { field: "id",                  headerName: "ID",                           width: 90 },

    // Meta
    { field: "tipoMaquinaControl",  headerName: "Tipo de Máquina y Control",    minWidth: 220, flex: 1 },
    { field: "numeroEquipo",        headerName: "Número de Equipo",             minWidth: 160, flex: 1 },
    { field: "fecha",               headerName: "Fecha",                        minWidth: 180, flex: 1 },

    // Analógicas
    { field: "tempProcesoEntrada",        headerName: "Temp. Proceso Entrada",         minWidth: 200, flex: 1 },
    { field: "tempProcesoSalida",         headerName: "Temp. Proceso Salida",          minWidth: 200, flex: 1 },
    { field: "tempCondensacionEntrada",   headerName: "Temp. Condensación Entrada",    minWidth: 230, flex: 1 },
    { field: "tempCondensacionSalida",    headerName: "Temp. Condensación Salida",     minWidth: 230, flex: 1 },
    { field: "tempDescargaCompresor",     headerName: "Temp. Descarga Compresor",      minWidth: 230, flex: 1 },
    { field: "tempRefrEvaporado",         headerName: "Temp. Refr Evaporado",          minWidth: 210, flex: 1 },
    { field: "presionSuccion",            headerName: "Presión Succión",               minWidth: 210, flex: 1 },
    { field: "presionDescarga",           headerName: "Presión Descarga",              minWidth: 210, flex: 1 },
    { field: "voltajeCmp",                headerName: "Voltaje Comp.",                 minWidth: 180, flex: 1 },
    { field: "amperajeCmp",               headerName: "Amperaje Comp.",                minWidth: 180, flex: 1 },
    { field: "sobrecalentamiento",        headerName: "Sobrecalentamiento",            minWidth: 210, flex: 1 },
    { field: "condicionActual",           headerName: "Condición Actual",              minWidth: 200, flex: 1 },
    { field: "porcentajeCarga",           headerName: "Porcentaje Carga (%)",          minWidth: 220, flex: 1 },

    // Digitales
    { field: "controlCarga",              headerName: "Control de Carga",              minWidth: 200, flex: 1 },
    { field: "ramploading",               headerName: "Ramp Loading",                  minWidth: 180, flex: 1 },
    { field: "switchFlujoAguaCondensacion", headerName: "Switch Flujo Agua Condensación", minWidth: 260, flex: 1 },
    { field: "switchFlujoAguaHelada",     headerName: "Switch Flujo Agua Helada",      minWidth: 230, flex: 1 },
    { field: "bajoNivelAceiteComp",       headerName: "Bajo Nivel Aceite Comp.",       minWidth: 220, flex: 1 },
    { field: "statusTempMotor",           headerName: "Status Temp Motor",             minWidth: 210, flex: 1 },
    { field: "interlock",                 headerName: "Interlock",                     minWidth: 180, flex: 1 },
    { field: "statusMotorProtect",        headerName: "Status MotorProtect",           minWidth: 210, flex: 1 },
    { field: "arranqueRemoto",            headerName: "Arranque Remoto",               minWidth: 200, flex: 1 },
    { field: "paroEmergencia",            headerName: "Paro de Emergencia",            minWidth: 200, flex: 1 },
    { field: "encendidoCompresor",        headerName: "Encendido Compresor",           minWidth: 210, flex: 1 },
    { field: "loadUnloadValvula1",        headerName: "Load/Unload Válvula 1",         minWidth: 220, flex: 1 },
    { field: "loadUnloadValvula2",        headerName: "Load/Unload Válvula 2",         minWidth: 220, flex: 1 },
    { field: "loadUnloadValvula3",        headerName: "Load/Unload Válvula 3",         minWidth: 220, flex: 1 },
    { field: "solenoideGasCaliente",      headerName: "Solenoide de Gas Caliente",     minWidth: 230, flex: 1 },
    { field: "inyeccionLiquidoSuccion",   headerName: "Inyección de Líquido en la Succión", minWidth: 280, flex: 1 },
  ];


  const dummyData = [
  { fecha: "1", value: 2 },
  { fecha: "2", value: 5.5 },
  { fecha: "3", value: 2 },
  { fecha: "5", value: 8.5 },
  { fecha: "8", value: 1.5 },
  { fecha: "10", value: 5 },
  ];

  // 1) Si tengo datos reales los uso, si no pongo un dummy
  const displayData = rows.length ? rows : dummyData;

  // 2) De mis opciones, cojo sólo las que estén marcadas
  const seriesKeys = React.useMemo(() => {
    const displaySeriesKeys = seriesOptions
      .filter(opt => selectedKeys.includes(opt.key))
      .map(opt => opt.key);

    return displaySeriesKeys.length
      ? displaySeriesKeys
      : (rows.length ? [] : ["value"]);
  }, [selectedKeys, rows.length]);

  // 4) Genero un color para cada serie
  const lineColors = React.useMemo(
    () => seriesKeys.map((_, i) => schemeCategory10[i % schemeCategory10.length]),
    [seriesKeys]
  );

  const labelMap = React.useMemo(() => {
  const m = Object.fromEntries(seriesOptions.map(o => [o.key, o.label]));
  m.value = "Valor (demo)";
  return m;
  }, []);


  // console.log("🔥 columns que llega a DataGrid:", columns);
    
     return (

       <Box sx={{ maxWidth: 1200, mx: "auto", mt: "90px", px: 2 }}>
      <Header
        title="Licuador 1 Comp"
        sxTitle={{ fontSize: "2rem", textAlign: "center" }}
      />

      {/* Top Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <TextField
              label="ID Equipo"
              variant="outlined"
              fullWidth
              value={idEquipo}
              onChange={(e) => setIdEquipo(e.target.value)}
              sx={textFieldStyles}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <TextField
              label="Fecha Inicio"
              variant="outlined"
              type="datetime-local"
              fullWidth
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={textFieldStyles}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <TextField
              label="Fecha Final"
              variant="outlined"
              type="datetime-local"
              fullWidth
              value={fechaFinal}
              onChange={(e) => setFechaFinal(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={textFieldStyles}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Middle Row: Variables con Tabs */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2, height: 400, display: "flex", flexDirection: "column" }}>
            {/* Tabs para Análogos | Digitales */}
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ mb: 1 }}
            >
              <Tab label="Análogos" />
              <Tab label="Digitales" />
            </Tabs>

            {/* Panel Análogos */}
            {tabIndex === 0 && (
              <Box sx={{ flex: 1, overflowY: "auto", px: 1, '& .MuiFormControlLabel-root': { mb: 1 } }}>
                <Grid container spacing={1}>
                  {seriesOptions
                    .filter(opt => analogKeys.includes(opt.key))
                    .map(opt => (
                      <Grid item xs={6} key={opt.key}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedKeys.includes(opt.key)}
                              onChange={() => toggleKey(opt.key)}
                            />
                          }
                          label={<Typography variant="caption"  sx={{ fontSize: 12, fontWeight: 'bold' }}>{opt.label}</Typography>}
                        />
                      </Grid>
                    ))
                  }
                </Grid>
              </Box>
            )}

            {/* Panel Digitales */}
            {tabIndex === 1 && (
              <Box sx={{ flex: 1, overflowY: "auto", px: 1, '& .MuiFormControlLabel-root': { mb: 1 } }}>
                <Grid container spacing={1}>
                  {seriesOptions
                    .filter(opt => digitalKeys.includes(opt.key))
                    .map(opt => (
                      <Grid item xs={6} key={opt.key}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedKeys.includes(opt.key)}
                              onChange={() => toggleKey(opt.key)}
                            />
                          }
                          label={<Typography variant="caption" fontWeight="bold">{opt.label}</Typography>}
                        />
                      </Grid>
                    ))
                  }
                </Grid>
              </Box>
            )}

            {/* Botón Graficar */}
            <Button
              variant="contained"
              sx={{
                bgcolor: "#771726",
                color: "white",
                mt: 1,
                width: isMobile ? "100%" : "auto",
                "&:hover": { bgcolor: "#747274" },
              }}
              onClick={(e) => { e.preventDefault(); fetchRows(); }}
            >
              Graficar
            </Button>
          </Paper>
        </Grid>

        {/* Table placeholder */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={1}
            sx={{
              height: 400,       // la altura fija del Paper
              p: 2,
              display: "flex",   // IMPORTANTE → convierte al Paper en un contenedor flex
              flexDirection: "column"
            }}
          >

            {/*
              Aquí le cedemos todo el espacio restante al DataGrid
              envolviéndolo en un Box con flex: 1.
            */}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <DataGrid
                rows={rows}
                columns={columns}
                loading={loading}
                disableColumnMenu
                pageSize={10}
                sx={{
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#771726",
                    zIndex: 1,
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: "#771726",
                    color: "#ffffff",
                    borderTop: "none",
                  },
                  "& .MuiTablePagination-root, & .MuiSvgIcon-root": {
                    color: "#ffffff",
                  },
                }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Final Row: Full‑width Chart */}
      <Grid container mb={3}>
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ height: 400, p: 2, pb: 4 }}>
            {loading ? (
              <Box sx={{display:"flex",justifyContent:"center",alignItems:"center",height:"100%"}}>
                <CircularProgress/>
              </Box>
            ) : (
              <>
                <Box
                  sx={{ 
                    display: "flex", 
                    overflowX: "auto", 
                    whiteSpace: "nowrap", 
                    mb: 1,                // espacio con el chart 
                    ml: "30px",           // desplazamiento igual al margin.left del chart 
                    width: "calc(100% - 60px)", // 100% menos left + right margins (20+30) 
                  }} 
                >
                  {seriesKeys.map((key, i) => (
                    <Box key={key} sx={{ display: "flex", alignItems: "center", mr: 3 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: lineColors[i],
                          mr: 1,
                        }}
                      />
                      <Typography variant="caption">{labelMap[key]}</Typography>
                    </Box>
                  ))}
                </Box>

                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    animationDuration={0}
                    data={displayData}
                    margin={{ top: 20, right: 30, left: -30, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="4 4" stroke={colors.grey[700]} />

                    <XAxis
                      dataKey="fecha"
                      interval="auto"
                      minTickGap={70}          
                      tickMargin={10}
                      tick={{ fill: colors.grey[300], fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: colors.grey[500] }}
                      tickFormatter={fmtNoSeconds} 
                    />
                    <YAxis
                      tick={{ fill: colors.grey[300], fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: colors.grey[500] }}
                      domain={["auto", "auto"]}
                    />

                    <Tooltip
                      content={<CustomTooltip />}
                      delayShow={0}
                      cursor={{ stroke: colors.grey[500], strokeDasharray: "3 3" }}
                    />

                    {/* Ya no va <Legend> aquí */}

                    {seriesKeys.map((key, i) => (
                      <Line
                        key={key}
                        dataKey={key}
                        name={labelMap[key]}
                        stroke={lineColors[i]}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 6 }}
                        isAnimationActive={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Error Snackbar */}
      <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GLicuador1Comp;